import apiUrl from './api';
import { getWithAuth, postWithAuth, postStreamWithAuth } from '@/lib/httpAdapter';

// Validate that apiUrl is not the localhost fallback (would indicate misconfiguration).
// During `next build`, this module can be imported while prerendering /home.
// Avoid crashing the build at module-evaluation time in that server-only path.
const isServerBuildPrerender =
  typeof window === 'undefined' &&
  process.env.NEXT_PHASE === 'phase-production-build';

function assertConfiguredObservabilityAuditApiUrl(): void {
  if (apiUrl === 'http://127.0.0.1:8000/' && !isServerBuildPrerender) {
    throw new Error(
      'observabilityAuditApi: API endpoint resolved to localhost fallback. ' +
        'This indicates missing or misconfigured NEXT_PUBLIC_ENVIRONMENT / NEXT_PUBLIC_LOCAL_ENDPOINT / NEXT_PUBLIC_PRODUCTION_ENDPOINT. ' +
        'All observability audit API calls will fail.'
    );
  }
}

const localHost = apiUrl;

// ---------------------------------------------------------------------------
// Shared / nested types
// ---------------------------------------------------------------------------

export interface BestPractice {
  title?: string;
  description?: string;
  practice?: string;
  details?: string;
  adopted?: boolean;
}

export interface CoverageTreeNode {
  name: string;
  path?: string;
  type: 'file' | 'directory';
  score: number;
  issues_count?: number;
  files_analyzed?: number;
  purpose?: string;
  children?: CoverageTreeNode[];
}

export interface AuditIssue {
  issue_id: string;
  severity: string;
  category: string;
  title: string;
  description: string;
  file_path: string;
  line_start: number;
  line_end: number;
  code_snippet: string;
  suggested_fix: string;
  fix_status: string;
  pr_number: number | null;
  pr_url: string;
}

export interface HighLevelRecommendation {
  title: string;
  description: string;
}

export interface AuditSummary {
  audit_id: string;
  repo_name: string;
  branch: string;
  scan_status: string;
  overall_score: number;
  grade: string;
  total_issues: number;
  created_at: string;
  completed_at: string;
}

// ---------------------------------------------------------------------------
// Request / Response interfaces
// ---------------------------------------------------------------------------

export interface StartAuditRequest {
  org_uuid: string;
  repo_name: string;
  access_token: string;
  branch?: string;
  repo_provider?: string;
  repo_host?: string;
}

export interface StartAuditResponse {
  success: boolean;
  audit_id: string;
  message: string;
}

export interface GetAuditStatusRequest {
  org_uuid: string;
  audit_id: string;
}

export interface GetAuditStatusResponse {
  success: boolean;
  audit_id: string;
  scan_status: string;
  progress_pct: number;
  current_step: string;
  error_message: string;
}

export interface GetAuditAssessmentRequest {
  org_uuid: string;
  audit_id: string;
}

export interface GetAuditAssessmentResponse {
  success: boolean;
  message: string;
  audit_id: string | null;
  overall_score: number | null;
  grade: string | null;
  files_analyzed: number | null;
  directories_analyzed: number | null;
  total_issues: number | null;
  best_practices: BestPractice[] | null;
  issues_summary: {
    total: number;
    by_severity: Record<string, number>;
    by_category: Record<string, number>;
  } | null;
  assessment_text: string | null;
  category_scores: Record<string, number> | null;
  estimated_savings: string | null;
  error: string | null;
}

export interface GetCoverageHeatmapRequest {
  org_uuid: string;
  audit_id: string;
}

export interface GetCoverageHeatmapResponse {
  success: boolean;
  audit_id: string;
  coverage_tree: CoverageTreeNode | CoverageTreeNode[];
}

export interface GetAuditIssuesRequest {
  org_uuid: string;
  audit_id: string;
  severity_filter?: string[];
  category_filter?: string[];
  page?: number;
  page_size?: number;
}

export interface GetAuditIssuesResponse {
  success: boolean;
  audit_id: string;
  issues: AuditIssue[];
  total_count: number;
  page: number;
  page_size: number;
}

export interface GetHighLevelRecsRequest {
  org_uuid: string;
  audit_id: string;
}

export interface GetHighLevelRecsResponse {
  success: boolean;
  message: string;
  audit_id: string | null;
  recommendations: HighLevelRecommendation[] | null;
  category_scores: Record<string, number> | null;
  error: string | null;
}

export interface AutoFixIssueRequest {
  org_uuid: string;
  audit_id: string;
  issue_ids: string[];
  access_token?: string;
}

export interface AutoFixIssueResponse {
  success: boolean;
  message: string;
  fix_results: Array<{
    issue_id: string;
    status: string;
    pr_url?: string;
    error?: string;
  }> | null;
  error: string | null;
}

export interface AutoFixStreamEvent {
  stage:
    | 'started'
    | 'reading_files'
    | 'generating_fixes'
    | 'fix_generated'
    | 'applying_fixes'
    | 'complete'
    | 'error';
  message: string;
  job_id?: string;
  total_issues?: number;
  total_files?: number;
  fixed?: number;
  failed?: number;
  fixes_ready?: number;
  issue_id?: string;
  file_path?: string;
  pr_url?: string;
  branch_name?: string;
  is_complete?: boolean;
}

export interface AutoFixJobStatus {
  active: boolean;
  stage?: string;
  message?: string;
  total_issues?: number;
  fixed?: number;
  failed?: number;
  pr_url?: string;
  is_complete?: boolean;
  job_id?: string;
}

export interface ListAuditsRequest {
  org_uuid: string;
  /** Optional server-side filter; the backend matches `repo_name` exactly. */
  repo_name?: string;
  /** Page size (backend max is 100). Defaults to 20 server-side when omitted. */
  limit?: number;
}

export interface ListAuditsResponse {
  success: boolean;
  audits: AuditSummary[];
}

export interface CancelAuditRequest {
  org_uuid: string;
  audit_id: string;
}

export interface CancelAuditResponse {
  success: boolean;
  message: string;
  audit_id: string | null;
  error: string | null;
}

export interface RegenerateSummaryRequest {
  org_uuid: string;
  audit_id: string;
}

export interface RegenerateSummaryResponse {
  success: boolean;
  message: string;
  audit_id: string | null;
  error: string | null;
}

export interface DismissIssuesRequest {
  org_uuid: string;
  audit_id: string;
  issue_ids: string[];
}

export interface DismissIssuesResponse {
  success: boolean;
  message: string;
  dismissed_count: number;
  error?: string;
}

export interface DismissRecommendationRequest {
  org_uuid: string;
  audit_id: string;
  recommendation_title: string;
}

export interface DismissRecommendationResponse {
  success: boolean;
  message: string;
  error?: string;
}

export interface WorkstreamFile {
  file_path: string;
  issue_title: string;
  line_refs: string;
  issue_id?: string;
  fix_status?: string;
  pr_url?: string;
  code_snippet?: string;
}

export interface WorkstreamSubGroup {
  area: string;
  description: string;
  files: WorkstreamFile[];
}

export interface IssueWorkstream {
  title: string;
  description: string;
  priority: string;
  estimated_effort: string;
  sub_groups: WorkstreamSubGroup[];
}

export interface GetGroupedIssuesRequest {
  org_uuid: string;
  audit_id: string;
}

export interface GetGroupedIssuesResponse {
  success: boolean;
  groups: IssueWorkstream[];
  total_issues: number;
  total_groups: number;
  error: string | null;
}

const startAuditEndpoint = `${localHost}observability_audit/start/`;
const getAuditStatusEndpoint = `${localHost}observability_audit/status/`;
const getAuditAssessmentEndpoint = `${localHost}observability_audit/assessment/`;
const getCoverageHeatmapEndpoint = `${localHost}observability_audit/coverage/`;
const getAuditIssuesEndpoint = `${localHost}observability_audit/issues/`;
const getHighLevelRecsEndpoint = `${localHost}observability_audit/high_level_recs/`;
const autoFixIssueEndpoint = `${localHost}observability_audit/auto_fix/`;
const autoFixStreamEndpoint = `${localHost}observability_audit/auto_fix_stream/`;
const autoFixStatusEndpoint = `${localHost}observability_audit/auto_fix_status/`;
const listAuditsEndpoint = `${localHost}observability_audit/list/`;
const cancelAuditEndpoint = `${localHost}observability_audit/cancel/`;
const getGroupedIssuesEndpoint = `${localHost}observability_audit/issues_grouped/`;
const regenerateSummaryEndpoint = `${localHost}observability_audit/regenerate_summary/`;
const dismissIssuesEndpoint = `${localHost}observability_audit/dismiss/`;
const dismissRecommendationEndpoint = `${localHost}observability_audit/dismiss_recommendation/`;

export async function startObservabilityAudit(
  req: StartAuditRequest
): Promise<StartAuditResponse> {
  assertConfiguredObservabilityAuditApiUrl();
  const response = await postWithAuth(startAuditEndpoint, req);
  return await response.json();
}

export async function getObservabilityAuditStatus(
  req: GetAuditStatusRequest
): Promise<GetAuditStatusResponse> {
  assertConfiguredObservabilityAuditApiUrl();
  const response = await postWithAuth(getAuditStatusEndpoint, req);
  return await response.json();
}

export async function getObservabilityAuditAssessment(
  req: GetAuditAssessmentRequest
): Promise<GetAuditAssessmentResponse> {
  assertConfiguredObservabilityAuditApiUrl();
  const response = await postWithAuth(getAuditAssessmentEndpoint, req);
  return await response.json();
}

export async function getObservabilityCoverageHeatmap(
  req: GetCoverageHeatmapRequest
): Promise<GetCoverageHeatmapResponse> {
  assertConfiguredObservabilityAuditApiUrl();
  const response = await postWithAuth(getCoverageHeatmapEndpoint, req);
  return await response.json();
}

export async function getObservabilityAuditIssues(
  req: GetAuditIssuesRequest
): Promise<GetAuditIssuesResponse> {
  assertConfiguredObservabilityAuditApiUrl();
  const response = await postWithAuth(getAuditIssuesEndpoint, req);
  return await response.json();
}

export async function getObservabilityHighLevelRecs(
  req: GetHighLevelRecsRequest
): Promise<GetHighLevelRecsResponse> {
  assertConfiguredObservabilityAuditApiUrl();
  const response = await postWithAuth(getHighLevelRecsEndpoint, req);
  return await response.json();
}

export async function autoFixObservabilityIssue(
  req: AutoFixIssueRequest
): Promise<AutoFixIssueResponse> {
  assertConfiguredObservabilityAuditApiUrl();
  const response = await postWithAuth(autoFixIssueEndpoint, req);
  return await response.json();
}

export async function listObservabilityAudits(
  req: ListAuditsRequest
): Promise<ListAuditsResponse> {
  assertConfiguredObservabilityAuditApiUrl();
  const response = await postWithAuth(listAuditsEndpoint, req);
  return await response.json();
}

export async function cancelObservabilityAudit(
  req: CancelAuditRequest
): Promise<CancelAuditResponse> {
  assertConfiguredObservabilityAuditApiUrl();
  const response = await postWithAuth(cancelAuditEndpoint, req);
  return await response.json();
}

export async function getGroupedIssues(
  req: GetGroupedIssuesRequest
): Promise<GetGroupedIssuesResponse> {
  assertConfiguredObservabilityAuditApiUrl();
  const response = await postWithAuth(getGroupedIssuesEndpoint, req);
  return await response.json();
}

export async function regenerateObservabilitySummary(
  req: RegenerateSummaryRequest
): Promise<RegenerateSummaryResponse> {
  assertConfiguredObservabilityAuditApiUrl();
  const response = await postWithAuth(regenerateSummaryEndpoint, req);
  return await response.json();
}

export async function dismissObservabilityIssues(
  req: DismissIssuesRequest
): Promise<DismissIssuesResponse> {
  assertConfiguredObservabilityAuditApiUrl();
  const response = await postWithAuth(dismissIssuesEndpoint, req);
  const data = await response.json();
  if (!data.success)
    throw new Error(data.error ?? data.message ?? 'Dismiss failed');
  return data;
}

export async function dismissObservabilityRecommendation(
  req: DismissRecommendationRequest
): Promise<DismissRecommendationResponse> {
  assertConfiguredObservabilityAuditApiUrl();
  const response = await postWithAuth(dismissRecommendationEndpoint, req);
  const data = await response.json();
  if (!data.success)
    throw new Error(data.error ?? data.message ?? 'Dismiss failed');
  return data;
}

export async function streamAutoFix(
  req: AutoFixIssueRequest,
  callbacks: {
    onEvent: (event: AutoFixStreamEvent) => void;
    onError: (error: Error) => void;
    onComplete: () => void;
  },
  signal?: AbortSignal
): Promise<void> {
  assertConfiguredObservabilityAuditApiUrl();
  const response = await postStreamWithAuth(autoFixStreamEndpoint, req, {
    signal,
  });

  if (!response.ok) {
    throw new Error(`Auto-fix stream failed: ${response.status}`);
  }

  if (!response.body) {
    throw new Error('Response body is null');
  }

  const reader = response.body.getReader();
  const decoder = new TextDecoder();
  let buffer = '';
  let readerReleased = false;

  const onAbort = () => {
    if (!readerReleased) {
      reader.cancel().catch(() => {});
    }
  };

  if (signal) {
    signal.addEventListener('abort', onAbort, { once: true });
  }

  try {
    while (true) {
      if (signal?.aborted) break;
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const lines = buffer.split('\n');
      buffer = lines.pop() || '';
      for (const line of lines) {
        if (!line.startsWith('data: ')) continue;
        const json = line.slice(6);
        if (!json.trim()) continue;
        try {
          const event = JSON.parse(json) as AutoFixStreamEvent;
          callbacks.onEvent(event);
          if (event.is_complete || event.stage === 'complete') {
            callbacks.onComplete();
            return;
          }
        } catch (e) {
          callbacks.onError(e as Error);
          return;
        }
      }
    }
    callbacks.onComplete();
  } catch (error) {
    callbacks.onError(error as Error);
  } finally {
    readerReleased = true;
    reader.releaseLock();
    if (signal) signal.removeEventListener('abort', onAbort);
  }
}

export async function getAutoFixJobStatus(
  jobId: string
): Promise<AutoFixJobStatus> {
  assertConfiguredObservabilityAuditApiUrl();
  const response = await getWithAuth(
    `${autoFixStatusEndpoint}?job_id=${encodeURIComponent(jobId)}`
  );
  return await response.json();
}
